/**
 * Traffic Source Chart Component
 *
 * Shows bookings and revenue by traffic source
 */

import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { TrafficSourceData } from "../types/web-types";
import { MetricTooltip } from "./metric-tooltip";

interface TrafficSourceChartProps {
  current: TrafficSourceData[];
  previous: TrafficSourceData[];
}

export function TrafficSourceChart({ current, previous }: TrafficSourceChartProps) {
  const { t } = useTranslation("reports");

  // Combine data for chart
  const chartData = current.map((curr) => {
    const prev = previous.find((p) => p.sourceId === curr.sourceId);
    return {
      name: curr.sourceName,
      currentBookings: curr.bookings,
      previousBookings: prev?.bookings || 0,
      currentRevenue: curr.revenue / 100, // Convert from cents to dollars
      previousRevenue: (prev?.revenue || 0) / 100,
    };
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center">
          {t("charts.trafficSources.title")}
          <MetricTooltip content={t("charts.trafficSources.tooltip")} />
        </CardTitle>
        <p className="text-xs text-muted-foreground">{t("charts.trafficSources.subtitle")}</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Bookings Chart */}
          <div>
            <h4 className="text-xs font-medium mb-2">{t("common.bookings")}</h4>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="name"
                  className="text-xs"
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                />
                <YAxis className="text-xs" tick={{ fontSize: 10 }} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    fontSize: "12px",
                  }}
                  labelStyle={{ fontWeight: 600 }}
                  formatter={(value: number, name: string) => {
                    if (name === "currentBookings") return [value, t("period.current")];
                    if (name === "previousBookings") return [value, t("period.previous")];
                    return [value, name];
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: "12px" }}
                  iconType="rect"
                  formatter={(value) =>
                    value === "currentBookings" ? t("period.current") : t("period.previous")
                  }
                />
                <Bar dataKey="currentBookings" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="previousBookings" fill="#94a3b8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Revenue Chart */}
          <div>
            <h4 className="text-xs font-medium mb-2">{t("common.revenue")}</h4>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="name"
                  className="text-xs"
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                />
                <YAxis className="text-xs" tick={{ fontSize: 10 }} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    fontSize: "12px",
                  }}
                  labelStyle={{ fontWeight: 600 }}
                  formatter={(value: number, name: string) => {
                    const label =
                      name === "currentRevenue" ? t("period.current") : t("period.previous");
                    return [`$${value.toFixed(2)}`, label];
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: "12px" }}
                  iconType="rect"
                  formatter={(value) =>
                    value === "currentRevenue" ? t("period.current") : t("period.previous")
                  }
                />
                <Bar dataKey="currentRevenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="previousRevenue" fill="#94a3b8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
